import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHashState } from '~/app/lib/use-hash-state';
import { useMediaQuery } from '~/app/lib/use-media-query';
import styles from './dynamic-grid.module.scss';

export const useModal = ({ modalId }: { modalId: string }) => {
  const [hash, setHash] = useHashState();
  const isModalOpen = useMemo(() => {
    return hash === modalId;
  }, [hash, modalId]);
  return { isModalOpen, setHash };
};

export const ModalContainer = ({
  children,
  closeButtonProps = {
    fontSize: '1.125rem',
    color: '#333',
  },
  modalHorizontalAlignment = 'center',
  modalId = 'modal',
  modalStyle = {
    backgroundColor: 'white',
    backgroundColorHex: '#ffffff',
  },
  modalSpacing = {
    marginTop: '0px',
    marginRight: '0px',
    marginBottom: '0px',
    marginLeft: '0px',
    paddingTop: '0px',
    paddingRight: '0px',
    paddingBottom: '0px',
    paddingLeft: '0px',
  },
  modalBorders = {
    border: 'none',
    borderColor: '#cdcdcd',
    borderWidth: '0px',
    borderStyle: 'none',
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
    borderBottomRightRadius: '0px',
    borderBottomLeftRadius: '0px',
    boxShadow: 'none',
    boxShadowColor: 'transparent',
  },
  modalTimeoutValue = 0,
  modalVerticalAlignment = 'center',
  shouldOpenOnMouseMove = false,
  showCloseButton = false,
}: {
  children: React.ReactNode;
  closeButtonProps?: any;
  modalHorizontalAlignment: string;
  modalId?: string; // Used for hash-based opening. Manual opening: setHash(modalId)
  modalStyle?: {
    backgroundColor: string;
    backgroundColorHex: string;
  };
  modalSpacing?: any;
  modalBorders?: any;
  modalTimeoutValue?: number; // Auto-trigger once after N seconds
  modalVerticalAlignment: string;
  shouldOpenOnMouseMove?: boolean; // Auto-trigger once on user interaction
  showCloseButton?: boolean;
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Tracks if automatic triggers (timeout/mouse movement) have fired once
  // Manual triggers (buttons, external setHash calls) can always open the modal
  const hasAutoTriggeredRef = useRef<boolean>(false);
  const scrollPositionRef = useRef<number>(0);
  const { isModalOpen, setHash } = useModal({ modalId });
  const screenSize = useMediaQuery();
  const isMobile = screenSize === 'sm';
  const isTablet = screenSize === 'md';

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        setHash('');
      }
    },
    [setHash]
  );

  const handleCloseClick = useCallback(() => {
    setHash('');
  }, [setHash]);

  // Lock/unlock background scrolling when modal opens/closes
  useEffect(() => {
    if (isModalOpen) {
      // Save current scroll position
      const currentScrollY = window.scrollY;
      scrollPositionRef.current = currentScrollY;

      // Prevent background scrolling with position fixed
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${currentScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      return () => {
        // Restore all styles
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';

        // Restore scroll position
        window.scrollTo(0, scrollPositionRef.current);
      };
    }

    return undefined;
  }, [isModalOpen]);

  useEffect(() => {
    // Only set up automatic opening logic if modal hasn't been auto-triggered yet
    if (!hasAutoTriggeredRef.current && !isModalOpen) {
      if (Number(modalTimeoutValue) > 0 && !shouldOpenOnMouseMove) {
        timeoutRef.current = setTimeout(() => {
          setHash(modalId);
          hasAutoTriggeredRef.current = true; // Mark as auto-triggered
        }, modalTimeoutValue * 1000);
      }

      const handleUserInteraction = () => {
        if (!hasAutoTriggeredRef.current) {
          setHash(modalId);
          hasAutoTriggeredRef.current = true; // Mark as auto-triggered
        }
      };

      if (shouldOpenOnMouseMove) {
        // Add listeners for pointer movement and scrolling
        window.addEventListener('mousemove', handleUserInteraction, {
          passive: true,
        });
        window.addEventListener('scroll', handleUserInteraction, {
          passive: true,
        });
        window.addEventListener('touchmove', handleUserInteraction, {
          passive: true,
        });
      }

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        if (shouldOpenOnMouseMove) {
          window.removeEventListener('mousemove', handleUserInteraction);
          window.removeEventListener('scroll', handleUserInteraction);
          window.removeEventListener('touchmove', handleUserInteraction);
        }
      };
    }

    // Always handle ESC key and cleanup when modal state changes
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        setHash('');
      }
    };

    if (isModalOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalId, isModalOpen, shouldOpenOnMouseMove, modalTimeoutValue]);

  if (!isModalOpen) {
    return null;
  }

  let spacing = modalSpacing;
  if (isMobile) spacing = modalSpacing.mobileSpacing || modalSpacing;
  if (isTablet) spacing = modalSpacing.tabletSpacing || modalSpacing;

  let borders = modalBorders;
  if (isMobile) borders = modalBorders.mobileBorders || modalBorders;
  if (isTablet) borders = modalBorders.tabletBorders || modalBorders;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={handleBackdropClick}
      className={styles.modalContainer}
      style={{
        alignItems: modalVerticalAlignment,
        justifyContent: modalHorizontalAlignment,
      }}
    >
      <div
        style={{
          position: 'relative',
          background:
            modalStyle.backgroundColorHex || modalStyle.backgroundColor,
          border: borders.border,
          borderColor: borders.borderColor || '#cdcdcd',
          borderTopLeftRadius: `${borders.borderTopLeftRadius}px`,
          borderTopRightRadius: `${borders.borderTopRightRadius}px`,
          borderBottomRightRadius: `${borders.borderBottomRightRadius}px`,
          borderBottomLeftRadius: `${borders.borderBottomLeftRadius}px`,
          borderStyle: borders.borderStyle,
          borderWidth: `${borders.borderWidth}px`,
          boxShadow: borders.boxShadow
            ? `0px 1px 10px -1px ${borders.boxShadowColor}`
            : undefined,
          marginTop: `${spacing.marginTop}px`,
          marginRight: `${spacing.marginRight}px`,
          marginBottom: `${spacing.marginBottom}px`,
          marginLeft: `${spacing.marginLeft}px`,
          paddingTop: `${spacing.paddingTop}px`,
          paddingRight: `${spacing.paddingRight}px`,
          paddingBottom: `${spacing.paddingBottom}px`,
          paddingLeft: `${spacing.paddingLeft}px`,
          maxHeight: '100%',
          overflowY: 'auto',
        }}
      >
        {showCloseButton && (
          <button
            type="button"
            onClick={handleCloseClick}
            style={{
              position: 'absolute',
              top: '0px',
              right: '0px',
              background: 'none',
              border: 'none',
              minWidth: '32px',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: `${closeButtonProps.fontSize}px`,
              fontWeight: 'bold',
              zIndex: 1000,
              color: closeButtonProps.color,
            }}
            aria-label="Close modal"
          >
            ×
          </button>
        )}

        {children}
      </div>
    </div>
  );
};
